import React, { useState, useEffect, useMemo } from "react";
import PersonalInfo from "../Components/PersonalInfo";
import ContactInfo from "../Components/ContactInfo";
import MedicalInfo from "../Components/MedicalInfo";
import FinancialInfo from "../Components/FinancialInfo";
import EstudiosInfo from "../Components/EstudiosInfo";
import Inscripcion from "../Components/Inscripcion";
import Loading from "../Components/Loading";
import "../css/nuevaSolicitud.css";
import { useDispatch, useSelector } from "react-redux";
import { obtenerSolicitudEstado } from "../../redux/solicitudesSlice";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import Archivos from "../Components/Archivos";
import { toast } from "react-toastify";

// 👇 nuevos imports para textos desde Strapi
import { getSolicitudPage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { StrapiError } from '../../Inicio/Pages/StrapiError';

function SolicitudInscripcion() {
  const [loading, setLoading] = useState(false);
  let navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const dispatch = useDispatch();
  const { solicitudEstado } = useSelector((state) => state.solicitudes);

  const etapaActual = solicitudEstado.result?.etapa;
  const [stepActual, setStepActual] = useState();

  // 👇 estado y locale para Strapi
  const { locale } = useLocale();
  const [solicitudPage, setSolicitudPage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [hasError, setHasError] = useState(false);

  // 👇 cargar textos desde Strapi por locale
  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    // Timeout para evitar carga eterna
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSolicitudPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 5000);
    
    getSolicitudPage(locale)
      .then((res) => {
        clearTimeout(timeoutId);
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setSolicitudPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        if (!cancel) {
          setSolicitudPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  // 👇 construir steps dinámicos en base a solicitudPage.stepbar
  const steps = useMemo(
    () => [
      {
        id: 1,
        titulo: solicitudPage?.stepbar?.personal_info ?? "Info personal",
        icono: "",
        url: "InfoPersonal",
      },
      {
        id: 2,
        titulo: solicitudPage?.stepbar?.contact ?? "Contacto",
        icono: "",
        url: "InfoContacto",
      },
      {
        id: 3,
        titulo: solicitudPage?.stepbar?.medical_info ?? "Info médica",
        icono: "",
        url: "InfoMedica",
      },
      {
        id: 4,
        titulo: solicitudPage?.stepbar?.financial_info ?? "Info financiera",
        icono: "",
        url: "InfoFinanciera",
      },
      {
        id: 5,
        titulo: solicitudPage?.stepbar?.studies ?? "Estudios",
        icono: "",
        url: "InfoEstudios",
      },
      {
        id: 6,
        titulo: solicitudPage?.stepbar?.inscription ?? "Inscripción",
        icono: "",
        url: "InfoInscripcion",
      },
      {
        id: 7,
        titulo: solicitudPage?.stepbar?.documents ?? "Documentos",
        icono: "",
        url: "InfoDocumentos",
      },
    ],
    [solicitudPage]
  );

  useEffect(() => {
    setLoading(true);
    dispatch(obtenerSolicitudEstado(id));
    const t = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(t);
  }, [dispatch, id]);

  useEffect(() => {
    if (etapaActual && solicitudPage) { // Solo ejecutar cuando solicitudPage esté cargado
      if (!solicitudEstado.result?.autorizado) {
        toast.error(solicitudEstado.message);
        navigate("/");
        return;
      }
      if (solicitudEstado.result?.estadoId == 9) {
        navigate("/");
      } else {
        const tab = steps.find((a) => a.id == etapaActual) ?? steps[0];
        if (tab) {
          navigate(`/Solicitud/${id}/${tab.url}`);
          setStepActual(etapaActual);
        }
      }
    }
  }, [etapaActual, solicitudPage, navigate, id, solicitudEstado]); // Removido steps de dependencias

  const handledTabClick = (item) => {
    navigate(`${item.url}`);
  };

  const toggle = (tab) => {
    navigate(`/Solicitud/${id}/${tab}`);
  };

  const verifyURL = (url) => {
    const parts = location.pathname.split("/");
    return parts[3] === url;
  };

  const validarEtapaActual = (item) => {
    let res = false;
    if (stepActual >= item.id) {
      res = true;
    }
    return res;
  };

  const actualizarEtapaActual = (etapa) => {
    setStepActual(etapa);
  };

  const stepLabel = solicitudPage?.stepbar?.step ?? "Paso";

  if (isLoadingContent) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <Skeleton active paragraph={{ rows: 4 }} />
            <Skeleton.Input style={{ width: '100%', height: 50 }} active />
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        </div>
      </div>
    );
  }

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSolicitudPage(null);
      }}
    />
  }

  return (
    <>
      <Loading
        clase={loading ? "loadingContainer" : "loadingContainer loadingHidden"}
      />
      <div className="principal-solicitud">
        <div className="steps">
          {steps.map((item, i) => (
            <div
              key={item.id}
              onClick={() => handledTabClick(item)}
              className={`${verifyURL(item.url) ? "actualStep" : ""} ${
                validarEtapaActual(item) ? "isActive" : ""
              }`}
            >
              <i className="material-icons">{item.icono}</i>
              <div>
                <label>{item.titulo}</label>
                <label className="stepDesc">
                  {/* concatenar el texto dinámico de Strapi con el índice */}
                  {`${stepLabel} ${i + 1}/${steps.length}`}
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="container-form">
          <Routes>
            <Route
              path={"InfoPersonal"}
              element={
                <PersonalInfo
                  handleStepChange={(item) => actualizarEtapaActual(item)}
                  solicitudId={id}
                  onGoNext={() => {
                    toggle("InfoContacto");
                  }}
                />
              }
            />
            <Route
              path="InfoContacto"
              element={
                <ContactInfo
                  handleStepChange={(item) => actualizarEtapaActual(item)}
                  solicitudId={id}
                  onGoBack={() => {
                    toggle("InfoPersonal");
                  }}
                  onGoNext={() => {
                    toggle("InfoMedica");
                  }}
                />
              }
            />
            <Route
              path="InfoMedica"
              element={
                <MedicalInfo
                  handleStepChange={(item) => actualizarEtapaActual(item)}
                  solicitudId={id}
                  onGoBack={() => {
                    toggle("InfoContacto");
                  }}
                  onGoNext={() => {
                    toggle("InfoFinanciera");
                  }}
                />
              }
            />
            <Route
              path="InfoFinanciera"
              element={
                <FinancialInfo
                  handleStepChange={(item) => actualizarEtapaActual(item)}
                  solicitudId={id}
                  onGoBack={() => {
                    toggle("InfoMedica");
                  }}
                  onGoNext={() => {
                    toggle("InfoEstudios");
                  }}
                />
              }
            />
            <Route
              path="InfoEstudios"
              element={
                <EstudiosInfo
                  handleStepChange={(item) => actualizarEtapaActual(item)}
                  solicitudId={id}
                  onGoBack={() => {
                    toggle("InfoFinanciera");
                  }}
                  onGoNext={() => {
                    toggle("InfoInscripcion");
                  }}
                />
              }
            />
            <Route
              path="InfoInscripcion"
              element={
                <Inscripcion
                  handleStepChange={(item) => actualizarEtapaActual(item)}
                  solicitudId={id}
                  onGoBack={() => {
                    toggle("InfoEstudios");
                  }}
                  onGoNext={() => {
                    navigate("InfoDocumentos");
                  }}
                />
              }
            />
            <Route
              path="InfoDocumentos"
              element={
                <Archivos
                  solicitudId={id}
                  onGoBack={() => {
                    toggle("InfoInscripcion");
                  }}
                  onGoNext={() => {
                    toggle("/");
                  }}
                />
              }
            />
            <Route path="*" element={<Navigate to="InfoPersonal" replace />} />
          </Routes>
        </div>
      </div>
    </>
  );
}

export default SolicitudInscripcion;
